import type {DiffLine} from './lineDiff';

export type SideBySideCellType = 'delete' | 'same' | 'empty' | 'add';
export type SideBySideCell = {content: string; type: SideBySideCellType; lineNum?: number};
export type SideBySideRow = {
  before: SideBySideCell;
  after: SideBySideCell;
  volatileStateChanged?: boolean;
};

const EMPTY: SideBySideCell = {content: '', type: 'empty'};

const deleteCell = (line: DiffLine): SideBySideCell => ({
  content: line.beforeContent ?? line.content,
  type: 'delete',
  lineNum: line.beforeLineNum
});

const addCell = (line: DiffLine): SideBySideCell => ({
  content: line.afterContent ?? line.content,
  type: 'add',
  lineNum: line.afterLineNum
});

/**
 * 行差分を左右並びの行に変換する。
 * 同じ行で区切られた変更のかたまりごとに、削除と追加を上から順に1対1で並べ、足りない側は空欄で埋める。
 * （Myers 法の結果は「削除…→追加…」の順に並ぶため、直後の1行だけを組にすると左右が1行ずれる）
 */
export const buildSideBySideRows = (diffLines: DiffLine[]): SideBySideRow[] => {
  const rows: SideBySideRow[] = [];
  let deletes: DiffLine[] = [];
  let adds: DiffLine[] = [];
  const flush = () => {
    for (let i = 0; i < Math.max(deletes.length, adds.length); i++) {
      rows.push({
        before: deletes[i] ? deleteCell(deletes[i]) : EMPTY,
        after: adds[i] ? addCell(adds[i]) : EMPTY
      });
    }
    deletes = [];
    adds = [];
  };
  for (const line of diffLines) {
    if (line.type === 'delete') {
      deletes.push(line);
    } else if (line.type === 'add') {
      adds.push(line);
    } else {
      flush();
      // 対応相手のない変更なし行（先頭の除外行の数が違うとき）は、相手側を空欄にする
      rows.push({
        before:
          line.beforeLineNum === undefined
            ? EMPTY
            : {
                content: line.beforeContent ?? line.content,
                type: 'same',
                lineNum: line.beforeLineNum
              },
        after:
          line.afterLineNum === undefined
            ? EMPTY
            : {
                content: line.afterContent ?? line.content,
                type: 'same',
                lineNum: line.afterLineNum
              },
        volatileStateChanged: line.volatileStateChanged
      });
    }
  }
  flush();
  return rows;
};
